/**
 * 0.45.33 - Search highlight, shared by the FAQ, tables and the
 * single / multi select dropdowns (it was the FAQ's own code in 0.45.30).
 *
 * The browser's CSS Custom Highlight API paints the matches
 * (::highlight(gt18-search) in global.css) without touching the DOM, so
 * React's text nodes stay as React made them. One named highlight for
 * the whole page, fed by every highlighting element on it. The colour is
 * the CSS variable --search-highlight-color on the element's root
 * (Henaket Warning light when unset). A browser without the API simply
 * shows no colour.
 *
 * Behaviour (0.45.35, the same everywhere): the matches are coloured
 * for as long as there is search text, and the colour goes when the
 * search is cleared. Clicks and scrolls never remove it, so paging or
 * sorting a table and opening FAQ questions keep it.
 */
import { useEffect, useLayoutEffect, useRef } from 'react';

const HL_NAME = 'gt18-search';
const registry = new Map();   // owner -> Range[]

function publish() {
  try {
    if (typeof CSS === 'undefined' || !CSS.highlights || typeof Highlight === 'undefined') return;
    const all = [];
    registry.forEach(list => all.push(...list));
    if (all.length) CSS.highlights.set(HL_NAME, new Highlight(...all));
    else CSS.highlights.delete(HL_NAME);
  } catch { /* no highlight support */ }
}

/** The owner's ranges (empty or null clears them). */
export function setHighlightRanges(owner, ranges) {
  if (ranges && ranges.length) registry.set(owner, ranges);
  else registry.delete(owner);
  publish();
}

/** Every case-insensitive match of `query` in the visible text under `root`. */
export function rangesFor(root, query) {
  const out = [];
  const q = String(query || '').trim().toLowerCase();
  if (!root || !q || typeof document === 'undefined') return out;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (n.parentElement && n.parentElement.closest('[hidden], [aria-hidden="true"]'))
      ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
  });
  let n;
  while ((n = walker.nextNode())) {
    const text = n.nodeValue.toLowerCase();
    let at = text.indexOf(q);
    while (at !== -1) {
      const r = document.createRange();
      r.setStart(n, at);
      r.setEnd(n, at + q.length);
      out.push(r);
      at = text.indexOf(q, at + q.length);
    }
  }
  return out;
}

/**
 * Colour the matches of `query` inside `rootRef.current` while `query`
 * is not empty. `enabled` false: nothing is coloured. Re-measured after
 * every render, so rows that appear, wrap or page in are covered.
 */
export function useSearchHighlight(rootRef, query, enabled = true) {
  const owner = useRef({});
  const q = String(query || '').trim();
  const live = useRef({ q, enabled });
  live.current = { q, enabled };
  // 0.45.35 - A child can change the text without this component
  // rendering (a table's page or sort, rows arriving): a
  // MutationObserver on the root re-measures then.
  const watch = useRef({ root: null, observer: null });
  const measure = () => {
    const { q: lq, enabled: le } = live.current;
    setHighlightRanges(owner.current, le && lq ? rangesFor(watch.current.root, lq) : null);
  };
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (root !== watch.current.root) {
      if (watch.current.observer) watch.current.observer.disconnect();
      watch.current.root = root;
      watch.current.observer = null;
      if (root && typeof MutationObserver !== 'undefined') {
        const ob = new MutationObserver(() => measure());
        ob.observe(root, { childList: true, subtree: true, characterData: true });
        watch.current.observer = ob;
      }
    }
    measure();
  });
  useEffect(() => () => {
    if (watch.current.observer) watch.current.observer.disconnect();
    // Forget the root too, so a remount (React's development double
    // run included) observes it again.
    watch.current = { root: null, observer: null };
    setHighlightRanges(owner.current, null);
  }, []);
}
